import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export const PlanAssistContext = createContext({ issues: {}, canEdit: false, replan: null });

/** Per-activity problems worth flagging, worked out from data the page already loaded. */
export function computeIssues({ leafItems, planByItem, actualByItem }) {
  const out = {};
  leafItems.forEach((item) => {
    const scope = Number(item.quantity || 0);
    const start = item.construction?.scheduled_start_date;
    const end = item.construction?.scheduled_end_date;
    const plan = planByItem[item.id] || {};
    const planTotal = Object.values(plan).reduce((s, v) => s + (Number(v) || 0), 0);
    const hasPlan = Object.keys(plan).length > 0;
    const negatives = (actualByItem[item.id] || []).filter((a) => Number(a.actual_qty) < 0);
    out[item.id] = {
      scope,
      noScope: scope <= 0,
      noUom: !item.uom,
      noDates: !start || !end,
      noPlan: scope > 0 && !!start && !!end && !hasPlan,
      planTotal,
      planMismatch: scope > 0 && hasPlan && Math.abs(planTotal - scope) > Math.max(0.01, scope * 0.001),
      negatives,
    };
  });
  return out;
}

function statusOf(scope, progress) {
  if (scope <= 0) return { label: "No scope", tone: "amber" };
  const planned = (progress?.planned_pct || 0) * 100;
  const actual = (progress?.actual_pct || 0) * 100;
  const gap = planned - actual;
  if (planned <= 0 && actual <= 0) return { label: "Not started", tone: "muted" };
  if (gap > 10) return { label: `Behind by ${gap.toFixed(0)} pts`, tone: "red" };
  if (gap > 0.5) return { label: `Slightly behind (${gap.toFixed(0)} pts)`, tone: "amber" };
  if (gap < -0.5) return { label: `Ahead by ${(-gap).toFixed(0)} pts`, tone: "green" };
  return { label: "On track", tone: "green" };
}

const PILL = {
  red: "bg-red-500/10 text-red-600 dark:text-red-400",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  muted: "bg-muted text-muted-foreground",
};

/** Status pill + plain-language flags, shown under the activity name. */
export function ActivityStatus({ item, progress }) {
  const { issues, canEdit, replan } = useContext(PlanAssistContext);
  const [busy, setBusy] = useState(false);
  const iss = issues[item.id];
  if (!iss) return null;
  const st = statusOf(iss.scope, progress);
  const notes = [];
  if (iss.noScope) notes.push(`Scope${iss.noUom ? " and UoM are" : " is"} missing - set ${iss.noUom ? "them" : "it"} in the WBS so progress can be measured.`);
  else if (iss.noUom) notes.push("UoM is missing.");
  if (iss.noDates) notes.push("No scheduled start/end date - the plan can't be spread.");
  if (iss.noPlan) notes.push("No plan yet.");
  if (iss.planMismatch) notes.push(`Plan adds up to ${fmtNum(iss.planTotal)} but scope is ${fmtNum(iss.scope)}.`);
  iss.negatives.forEach((n) => notes.push(`Negative actual ${fmtNum(n.actual_qty)} on ${fmtDate(n.date)} - check the cumulative entries.`));
  const canReplan = canEdit && replan && !iss.noScope && !iss.noDates && (iss.planMismatch || iss.noPlan);

  return (
    <div className="mt-1 flex flex-col gap-0.5" data-testid={`plan-status-${item.id}`}>
      <span className={`inline-flex w-fit rounded px-1.5 py-0.5 text-[10px] font-semibold ${PILL[st.tone]}`}>{st.label}</span>
      {notes.map((n, i) => <span key={i} className="max-w-[240px] text-[10px] leading-snug text-amber-600 dark:text-amber-400">{n}</span>)}
      {canReplan && (
        <button
          type="button"
          disabled={busy}
          onClick={async () => { setBusy(true); try { await replan(item.id); } finally { setBusy(false); } }}
          className="w-fit text-[10px] font-semibold text-gold-600 underline underline-offset-2 hover:opacity-80 dark:text-gold-400"
          data-testid={`plan-regenerate-${item.id}`}
        >
          {busy ? "Regenerating…" : "Regenerate plan from scope & dates"}
        </button>
      )}
    </div>
  );
}

export function PlanHint({ view, canEdit }) {
  return (
    <p className="text-xs text-muted-foreground" data-testid="plan-hint">
      {view === "weekly"
        ? "Weekly view is read-only. Switch to Daily to edit planned quantities."
        : canEdit ? "Type planned quantities in the P row, then press Save changes. Plans are created automatically from each activity's scope and dates." : "Planned quantities are shown read-only."}
      {" "}Actual quantities come from the construction progress entries.
    </p>
  );
}

/** Header button: fill the plan for every activity that has scope + dates but no plan yet. */
export function AutoPlanButton({ projectId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/plan-entries/auto-fill`, { method: "linear" });
      if (data.entries_created > 0) toast.success(`Plan created for ${data.items_processed} activit${data.items_processed === 1 ? "y" : "ies"}`);
      else toast.info("Nothing to fill - every activity either has a plan already or is missing scope/dates (see the flags on each row).");
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="plan-autofill-button">
      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
      Auto-plan
    </Button>
  );
}
